import { MarketingShell } from "@/components/landing/MarketingShell";
import { useSession } from "@/components/useSession";
import { useRouter } from "next/router";
import { useState } from "react";
import useSWR, { mutate } from "swr";
import {
  AppWindow,
  ArrowRight,
  Check,
  CreditCard,
  Loader2,
  Plus,
  Server,
  Users,
  Users2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import logger from "@/lib/logger";
import {
  capitalize,
  cn,
  fetcherTyped,
  formatDate,
  formatMessage,
  okstatus
} from "@/lib/utils";
import { TenantMembershipListSchema } from "@/types/schemas";
import z from "zod";

export default function TeamPage() {
  const { data: session, update, status } = useSession({ required: true });
  const router = useRouter();
  const { data: tenants } = useSWR("/api/tenant", fetcherTyped(TenantMembershipListSchema));
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSwitch = async (tenantId: string, destination = "/dashboard") => {
    if (loading) {
      return;
    }
    setLoading(true);
    try {
      await fetch("/api/tenant", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId })
      })
        .then(okstatus)
        .then(res => res.json());
      await update();
      await mutate(() => true, undefined, { revalidate: true });
      router.push(destination);
    } catch (error) {
      logger.error("Failed to switch team", error);
      toast({ title: "Failed to switch team", variant: "destructive" });
      setLoading(false);
    }
  };

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (loading || !trimmed) {
      return;
    }
    setLoading(true);
    try {
      await fetch("/api/tenant", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: trimmed })
      })
        .then(okstatus)
        .then(res => res.json());
      await update();
      await mutate(() => true, undefined, { revalidate: true });
      router.push("/dashboard");
    } catch (error) {
      logger.error("Failed to create team", error);
      toast({
        title: "Could not create team",
        description: formatMessage(error),
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  return (
    <MarketingShell>
      <div className="mx-auto mt-10 md:mt-20 md:mb-10 flex w-full max-w-3xl flex-col gap-6 px-4 md:px-0">
        <div className="flex flex-col space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Your teams</h1>
          <p className="text-balance text-sm text-muted-foreground">
            Select a team to continue, or create a new one.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {tenants?.map(tenant => (
            <TenantCard
              key={tenant.tenantId}
              tenant={tenant}
              loading={loading}
              handleSwitch={handleSwitch}
              className={tenants.length === 1 ? "col-span-full" : undefined}
            />
          ))}

          {tenants && tenants.length === 0 && (
            <p className="col-span-full text-center text-sm text-muted-foreground">
              You are not a member of any team yet.
            </p>
          )}
        </div>

        <form
          onSubmit={handleCreate}
          className="flex flex-col gap-2 border rounded-lg p-4 border-border pt-4"
        >
          <Label htmlFor="teamName" className="text-lg font-semibold">
            Create a new team
          </Label>
          <div className="flex gap-2">
            <Input
              id="teamName"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Team name"
              aria-label="New team name"
              disabled={loading}
            />
            <Button type="submit" disabled={loading || !name.trim()}>
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              Create
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            To create a new team, you must have a subscription to a paid plan in one of your
            existing teams.
          </p>
        </form>
      </div>
    </MarketingShell>
  );
}

export const TenantCard = ({
  tenant,
  loading,
  handleSwitch,
  className
}: {
  tenant: z.output<typeof TenantMembershipListSchema>[number];
  loading: boolean;
  handleSwitch: (tenantId: string, link?: string) => Promise<void>;
  className?: string;
}) => (
  <div
    key={tenant.tenantId}
    className={cn(
      "flex min-h-[12rem] flex-col rounded-lg border border-border transition-colors cursor-pointer",
      "bg-card group",
      className
    )}
  >
    <button
      type="button"
      disabled={loading}
      aria-label={`Choose ${tenant.name}`}
      onClick={() => handleSwitch(tenant.tenantId)}
      className={cn(
        "flex grow flex-col gap-4 rounded-t-lg p-6 text-left transition-colors cursor-pointer hover:bg-accent"
      )}
    >
      <div className="flex items-center gap-2">
        <Users2 className="h-6 w-6 shrink-0 text-muted-foreground" />
        <span className="grow truncate text-xl font-semibold">{tenant.name}</span>
        {tenant.current ? <Check className="h-5 w-5 shrink-0 text-primary" /> : null}
      </div>

      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <span className="flex items-center gap-1.5" title="Users">
          <Users className="h-4 w-4" />
          {tenant.users}
        </span>
        <span className="flex items-center gap-1.5" title="Hosts">
          <Server className="h-4 w-4" />
          {tenant.hosts}
        </span>
        <span className="flex items-center gap-1.5" title="Apps">
          <AppWindow className="h-4 w-4" />
          {tenant.apps}
        </span>
        {tenant.planId ? (
          <span className="flex items-center gap-1.5" title="Plan">
            <CreditCard className="h-4 w-4" />
            {capitalize(
              tenant.planId
                .replace(/^(monthly|yearly):/, "")
                .split(":")
                .slice(0, 1)
                .join(" ")
            )}
          </span>
        ) : null}
      </div>

      <div className="mt-auto flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          Created {formatDate(tenant.createdAt, "MMM d, yyyy")}
        </span>

        <span className="flex items-center gap-1 text-sm font-medium text-primary">
          {tenant.current ? "Current team" : "Choose team"}
          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
        </span>
      </div>
    </button>

    <div className="flex border-t border-border">
      <button
        type="button"
        disabled={loading}
        aria-label={`Open subscription settings for ${tenant.name}`}
        onClick={() => handleSwitch(tenant.tenantId, "/settings/subscription")}
        className={cn(
          "flex flex-1 items-center justify-center gap-1.5 rounded-bl-lg cursor-pointer",
          "p-4 text-sm font-medium text-muted-foreground transition-colors",
          "hover:bg-accent"
        )}
      >
        <CreditCard className="h-4 w-4" />
        Subscription
      </button>
      <button
        type="button"
        disabled={loading}
        aria-label={`Open user settings for ${tenant.name}`}
        onClick={() => handleSwitch(tenant.tenantId, "/settings/users")}
        className={cn(
          "flex flex-1 items-center justify-center gap-1.5 rounded-br-lg cursor-pointer border-l border-border",
          "p-4 text-sm font-medium text-muted-foreground transition-colors",
          "hover:bg-accent"
        )}
      >
        <Users className="h-4 w-4" />
        Users
      </button>
    </div>
  </div>
);
